/**
 * Arc Wire Tool - BIM 2025 Grade
 * 
 * Creates schematic electrical wiring (Arc/Spline) between devices.
 * Supports:
 * - Arc/Chamfer/Spline geometry
 * - Home run arrows
 * - Tick marks (Hot, Neutral, Ground)
 * - Auto-snap to connectors
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export type WireType = 'Arc' | 'Chamfer' | 'Spline';

export interface WireGeometryData {
    points: [number, number, number][]; // Calculated curve points
    startDeviceId?: string;
    endDeviceId?: string;
    isHomeRun: boolean;
    tickMarks: {
        hot: number;
        neutral: number;
        ground: number;
    };
}

export interface WireElement extends Element {
    category: 'Wires';
    geometryData: WireGeometryData;
}

export class ArcWireTool extends BaseTool {
    id = 'arc_wire';
    name = 'Arc Wire';
    cursor = 'crosshair';

    private startDeviceId: string | null = null;
    private startPoint: THREE.Vector3 | null = null;

    private currentPoint: THREE.Vector3 | null = null;

    // Options
    // private _wireType: WireType = 'Arc';
    private isHomeRun: boolean = false;
    private numHot: number = 1;
    private numNeutral: number = 1;
    private numGround: number = 1;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.getElectricalHit(event);

        if (hit) {
            // Clicked on a device
            const deviceId = this.findId(hit.object)!;
            const point = hit.point;

            if (!this.startDeviceId) {
                // Start wire
                this.startDeviceId = deviceId;
                this.startPoint = point;
            } else {
                // End wire at device
                this.createWire(this.startPoint!, point, this.startDeviceId, deviceId);
                this.reset();
            }
        } else {
            // Clicked in empty space
            if (this.startDeviceId && this.startPoint) {
                // Create Home Run to this point direction
                const point = this.getIntersection(event);
                if (point) {
                    this.isHomeRun = true;
                    this.createWire(this.startPoint!, point, this.startDeviceId, undefined);
                    this.reset();
                }
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const hit = this.getElectricalHit(event);
        const point = hit ? hit.point : this.getIntersection(event);

        if (point) {
            this.currentPoint = point;
            this.updatePreview();
        }
    }

    private getElectricalHit(event: ThreeEvent<PointerEvent>) {
        return event.intersections.find(i => {
            const id = this.findId(i.object);
            if (!id) return false;
            const el = this.context!.bimStore.project.elements.get(id);
            return el && (
                el.category === 'Electrical Fixtures' ||
                el.category === 'Lighting Fixtures' ||
                el.category === 'Electrical Equipment' ||
                el.category === 'Electrical Devices'
            );
        });
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
        }
    }

    private reset() {
        this.startDeviceId = null;
        this.startPoint = null;
        this.isHomeRun = false;
        this.context?.appStore.setToolPreview(null);
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            // Calculate Curve
            const curvePoints = this.calculateWireCurve(this.startPoint, this.currentPoint);

            this.context.appStore.setToolPreview({
                type: 'wire_arc',
                points: curvePoints,
                color: '#000000'
            });
        }
    }

    private createWire(start: THREE.Vector3, end: THREE.Vector3, startId: string, endId?: string) {
        if (!this.context) return;

        const curvePoints = this.calculateWireCurve(start, end);

        const geometryData: WireGeometryData = {
            points: curvePoints.map(p => [p.x, p.y, p.z] as [number, number, number]),
            startDeviceId: startId,
            endDeviceId: endId,
            isHomeRun: this.isHomeRun,
            tickMarks: {
                hot: this.numHot,
                neutral: this.numNeutral,
                ground: this.numGround
            }
        };

        const wire: WireElement = {
            id: uuidv4(),
            category: 'Wires',
            type: this.isHomeRun ? 'Wire: Home Run' : 'Wire: Arc',
            parameters: {
                'Wire Type': 'THWN',
                'Size': '#12 AWG',
                'Circuits': '1'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(wire);
    }

    private calculateWireCurve(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
        // Simple Quadratic Bezier for Arc
        // Control point is midpoint + upward/outward offset

        const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        const dist = start.distanceTo(end);

        // Offset perpendicular to the line connecting start-end, in the XY plane
        const dir = new THREE.Vector3().subVectors(end, start).normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0); // Z=0 constraint for floor plan view

        // Valid for plan view wire
        const control = mid.clone().add(perp.multiplyScalar(dist * 0.5));

        const curve = new THREE.QuadraticBezierCurve3(start, control, end);
        return curve.getPoints(20);
    }
}
